import InputText from "./InputText";
import InputSelect from "./InputSelect";
import InputRange from "./InputRange";
import Loader from "../../../Components/Loader/Button";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../../config/api";
import {
  calculateEstimatedTradePnlDetails,
  findSymbolData,
  formatTrimmedNumber,
  getEstimatedTradePnlMeta,
  getTpSlValidationMessages,
  inferSymbolPricePrecision,
  isValidDecimalInput,
  resolveSideMarketPrice,
  resolveTradeEntryPrice,
} from "../tradeUtils";

const normalizeDecimalInput = (value) =>
  String(value ?? "")
    .replace(/,/g, "")
    .trim();

const toNullableNumber = (value) => {
  const normalized = normalizeDecimalInput(value);

  if (!normalized || normalized === ".") {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatGroupedInteger = (value) =>
  value.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const formatGroupedDecimalInput = (value) => {
  const normalized = normalizeDecimalInput(value);

  if (!normalized) {
    return "";
  }

  const hasDecimal = normalized.includes(".");
  const [integerPart = "", decimalPart = ""] = normalized.split(".");
  const formattedInteger = integerPart ? formatGroupedInteger(integerPart) : "";

  if (!hasDecimal) {
    return formattedInteger;
  }

  if (!integerPart) {
    return `.${decimalPart}`;
  }

  return `${formattedInteger}.${decimalPart}`;
};

const PENDING_PAYOUT_ORDER_BLOCK_DETAIL =
  "Account has a pending payout and cannot open new orders.";

export default function InputsOrder({
  accountData,
  calculatorInsertRequest,
  marketData,
  symbol,
  selectedSide,
  onTradeNotification,
}) {
  const navigate = useNavigate();
  const { accountId: accountCode } = useParams();
  const [orderType, setOrderType] = useState("Limit");
  const [percentValue, setPercentValue] = useState(0); // 0–100 (float)
  const [volume, setVolume] = useState(""); // USDT (string for input)
  const [volumeDisplay, setVolumeDisplay] = useState("");
  const [limitPrice, setLimitPrice] = useState(null);
  const [limitPriceDisplay, setLimitPriceDisplay] = useState("");
  const [slStopPrice, setSlStopPrice] = useState(null);
  const [slStopPriceDisplay, setSlStopPriceDisplay] = useState("");
  const [slLimitPrice, setSlLimitPrice] = useState(null);
  const [slLimitPriceDisplay, setSlLimitPriceDisplay] = useState("");
  const [smStopPrice, setSmStopPrice] = useState(null);
  const [smStopPriceDisplay, setSmStopPriceDisplay] = useState("");

  const [slPrice, setSlPrice] = useState(null);
  const [slPriceDisplay, setSlPriceDisplay] = useState("");
  const [tpPrice, setTpPrice] = useState(null);
  const [tpPriceDisplay, setTpPriceDisplay] = useState("");
  const [canTrade, setCanTrade] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const volumeChangeSourceRef = useRef(null);
  const handledCalculatorInsertRequestIdRef = useRef(null);

  const resetInputs = () => {
    volumeChangeSourceRef.current = null;
    setPercentValue(0);
    setVolume("");
    setVolumeDisplay("");
    setLimitPrice(null);
    setLimitPriceDisplay("");
    setSlStopPrice(null);
    setSlStopPriceDisplay("");
    setSlLimitPrice(null);
    setSlLimitPriceDisplay("");
    setSmStopPrice(null);
    setSmStopPriceDisplay("");
    setSlPrice(null);
    setSlPriceDisplay("");
    setTpPrice(null);
    setTpPriceDisplay("");
    setCanTrade(false);
  };

  useEffect(() => {
    // Reset all inputs when symbol changes.
    resetInputs();
  }, [symbol]);

  const selectedSymbolData = useMemo(
    () => findSymbolData(marketData?.symbols, symbol),
    [marketData, symbol],
  );
  const currentMarketPrice = useMemo(
    () => resolveSideMarketPrice(selectedSymbolData, selectedSide),
    [selectedSymbolData, selectedSide],
  );
  const hasMarketPrice = currentMarketPrice !== null;
  const maxPriceDecimals = inferSymbolPricePrecision(selectedSymbolData, 4);

  const freeMargin = accountData?.account?.free_margin ?? 0;
  const leverage = accountData?.account?.leverage ?? 0;
  const VOLUME_DECIMALS = 2; // how many decimal places for volume
  const volumeNumber = Number(normalizeDecimalInput(volume));
  const safeVolume = Number.isFinite(volumeNumber) ? volumeNumber : 0;
  const entryPrice = useMemo(
    () =>
      resolveTradeEntryPrice({
        orderType,
        marketPrice: currentMarketPrice,
        limitPrice: orderType === "Limit" ? limitPrice : slLimitPrice,
        stopPrice: smStopPrice,
      }),
    [orderType, currentMarketPrice, limitPrice, slLimitPrice, smStopPrice],
  );
  const estimatedTpPnlDetails = useMemo(() => {
    if (!canTrade || !tpPrice) return null;

    return calculateEstimatedTradePnlDetails({
      side: selectedSide,
      entryPrice,
      targetPrice: tpPrice,
      marginUsdt: safeVolume,
      leverage,
    });
  }, [canTrade, tpPrice, selectedSide, entryPrice, safeVolume, leverage]);
  const estimatedSlPnlDetails = useMemo(() => {
    if (!canTrade || !slPrice) return null;

    return calculateEstimatedTradePnlDetails({
      side: selectedSide,
      entryPrice,
      targetPrice: slPrice,
      marginUsdt: safeVolume,
      leverage,
    });
  }, [canTrade, slPrice, selectedSide, entryPrice, safeVolume, leverage]);
  const estimatedTpPnl = estimatedTpPnlDetails?.pnl ?? null;
  const estimatedSlPnl = estimatedSlPnlDetails?.pnl ?? null;
  const estimatedTpPnlMeta = getEstimatedTradePnlMeta(estimatedTpPnl, {
    liquidated: estimatedTpPnlDetails?.liquidated,
  });
  const estimatedSlPnlMeta = getEstimatedTradePnlMeta(estimatedSlPnl, {
    liquidated: estimatedSlPnlDetails?.liquidated,
  });
  const tpSlValidationMessages = useMemo(
    () =>
      getTpSlValidationMessages({
        side: selectedSide,
        marketPrice: currentMarketPrice,
        tpPrice,
        slPrice,
      }),
    [currentMarketPrice, selectedSide, slPrice, tpPrice],
  );
  const formatEstimatedPnl = (value) =>
    formatTrimmedNumber(Math.abs(value), {
      useGrouping: true,
      fallback: "-",
      maxFractionDigits: 2,
    });
  const handlePriceChange = useCallback(
    (value, setPrice, setDisplay) => {
      const normalized = normalizeDecimalInput(value);

      if (!isValidDecimalInput(normalized, maxPriceDecimals)) {
        return;
      }

      setDisplay(formatGroupedDecimalInput(normalized));
      setPrice(toNullableNumber(normalized));
    },
    [maxPriceDecimals],
  );

  const handlePercentChange = (nextPercent) => {
    volumeChangeSourceRef.current = "slider";
    setPercentValue(nextPercent);
  };

  const handleVolumeChange = (e) => {
    const v = normalizeDecimalInput(e.target.value);

    if (!/^\d*\.?\d{0,2}$/.test(v)) return;

    volumeChangeSourceRef.current = "input";
    setVolume(v);
    setVolumeDisplay(formatGroupedDecimalInput(v));

    if (!freeMargin) {
      setPercentValue(0);
      return;
    }

    const parsed = Number(v);
    const safeParsed = Number.isFinite(parsed) ? parsed : 0;
    if (safeParsed > freeMargin) {
      const factor = 10 ** VOLUME_DECIMALS;
      const rounded = Math.round(freeMargin * factor) / factor;

      setVolume(String(rounded));
      setVolumeDisplay(formatGroupedDecimalInput(rounded));
      setPercentValue(100);
      return;
    }
    const nextPercent = (safeParsed / freeMargin) * 100;
    const clamped = Math.min(100, Math.max(0, nextPercent));

    setPercentValue(clamped);
  };

  const applyVolumeValue = useCallback(
    (rawValue) => {
      const normalized = normalizeDecimalInput(rawValue);

      if (!/^\d*\.?\d{0,2}$/.test(normalized)) return;

      const parsed = Number(normalized);
      const safeParsed = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
      const limitedValue = freeMargin
        ? Math.min(safeParsed, Number(freeMargin))
        : safeParsed;
      const factor = 10 ** VOLUME_DECIMALS;
      const rounded = Math.round(limitedValue * factor) / factor;

      volumeChangeSourceRef.current = "input";
      setVolume(String(rounded));
      setVolumeDisplay(formatGroupedDecimalInput(rounded));

      if (!freeMargin) {
        setPercentValue(0);
        return;
      }

      const nextPercent = (rounded / freeMargin) * 100;
      const clamped = Math.min(100, Math.max(0, nextPercent));

      setPercentValue(clamped);
    },
    [VOLUME_DECIMALS, freeMargin],
  );

  useEffect(() => {
    if (!safeVolume) {
      setCanTrade(false);
      return;
    }
    if (!accountData || !selectedSymbolData || !hasMarketPrice) {
      setCanTrade(false);
      return;
    }

    if (orderType === "Limit" && !limitPrice) {
      setCanTrade(false);
      return;
    }
    if (orderType === "StopLimit" && (!slLimitPrice || !slStopPrice)) {
      setCanTrade(false);
      return;
    }
    if (orderType === "StopMarket" && !smStopPrice) {
      setCanTrade(false);
      return;
    }
    if (tpSlValidationMessages.tp) {
      setCanTrade(false);
      return;
    }
    if (tpSlValidationMessages.sl) {
      setCanTrade(false);
      return;
    }
    setCanTrade(true);
  }, [
    volume,
    safeVolume,
    accountData,
    orderType,
    limitPrice,
    slLimitPrice,
    slStopPrice,
    smStopPrice,
    selectedSide,
    tpPrice,
    slPrice,
    symbol,
    selectedSymbolData,
    hasMarketPrice,
    currentMarketPrice,
    tpSlValidationMessages,
  ]);

  // when percent changes (slider / external), update volume
  useEffect(() => {
    if (!freeMargin) {
      setVolume("0");
      setVolumeDisplay("0");
      return;
    }

    if (volumeChangeSourceRef.current === "input") {
      volumeChangeSourceRef.current = null;
      return;
    }

    const raw = (freeMargin * percentValue) / 100;
    const factor = 10 ** VOLUME_DECIMALS;
    const rounded = Math.round(raw * factor) / factor;

    setVolume(String(rounded));
    setVolumeDisplay(formatGroupedDecimalInput(rounded));
    volumeChangeSourceRef.current = null;
  }, [percentValue, freeMargin]);

  useEffect(() => {
    if (!calculatorInsertRequest?.id) return;
    if (
      handledCalculatorInsertRequestIdRef.current === calculatorInsertRequest.id
    ) {
      return;
    }

    handledCalculatorInsertRequestIdRef.current = calculatorInsertRequest.id;

    const insertValues =
      calculatorInsertRequest.values &&
      typeof calculatorInsertRequest.values === "object"
        ? calculatorInsertRequest.values
        : {
            [calculatorInsertRequest.field]: calculatorInsertRequest.value,
          };

    if (Object.prototype.hasOwnProperty.call(insertValues, "volume")) {
      applyVolumeValue(String(insertValues.volume ?? ""));
    }

    if (Object.prototype.hasOwnProperty.call(insertValues, "sl")) {
      handlePriceChange(
        String(insertValues.sl ?? ""),
        setSlPrice,
        setSlPriceDisplay,
      );
    }
  }, [applyVolumeValue, calculatorInsertRequest, handlePriceChange]);

  const handleOrder = async () => {
    if (!canTrade || isSubmittingOrder) return;
    const openOrdersCount = Array.isArray(accountData?.orders?.open)
      ? accountData.orders.open.length
      : 0;
    const openPositionsCount = Array.isArray(accountData?.positions?.open)
      ? accountData.positions.open.length
      : 0;

    if (openOrdersCount + openPositionsCount >= 10) {
      if (typeof onTradeNotification === "function") {
        onTradeNotification({
          type: "open_order_limit",
          tone: "warning",
          message: "همزمان حداکثر ده پوزیشن و سفارش باز می توان داشت.",
        });
      }
      return;
    }

    var orderInput;
    if (orderType === "Market") {
      orderInput = {
        order_type: "market",
        symbol: symbol,
        side: selectedSide === "buy" ? "long" : "short",
        margin_usdt: safeVolume,
        account_id: accountCode,
      };
      if (tpPrice) {
        orderInput.take_profit = Number(tpPrice);
      }
      if (slPrice) {
        orderInput.stop_loss = Number(slPrice);
      }
    } else if (orderType === "Limit") {
      orderInput = {
        order_type: "limit",
        symbol: symbol,
        side: selectedSide === "buy" ? "long" : "short",
        margin_usdt: safeVolume,
        account_id: accountCode,
        limit_price: Number(limitPrice),
      };
      if (tpPrice) {
        orderInput.take_profit = Number(tpPrice);
      }
      if (slPrice) {
        orderInput.stop_loss = Number(slPrice);
      }
    } else if (orderType === "StopLimit") {
      orderInput = {
        order_type: "stop_limit",
        symbol: symbol,
        side: selectedSide === "buy" ? "long" : "short",
        margin_usdt: safeVolume,
        account_id: accountCode,
        stop_price: Number(slStopPrice),
        limit_price: Number(slLimitPrice),
      };
      if (tpPrice) {
        orderInput.take_profit = Number(tpPrice);
      }
      if (slPrice) {
        orderInput.stop_loss = Number(slPrice);
      }
    } else if (orderType === "StopMarket") {
      orderInput = {
        order_type: "stop_market",
        symbol: symbol,
        side: selectedSide === "buy" ? "long" : "short",
        margin_usdt: safeVolume,
        account_id: accountCode,
        stop_price: Number(smStopPrice),
      };
      if (tpPrice) {
        orderInput.take_profit = Number(tpPrice);
      }
      if (slPrice) {
        orderInput.stop_loss = Number(slPrice);
      }
    }
    setIsSubmittingOrder(true);
    try {
      let response = await fetch(
        `${API_BASE_URL}trading/open-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
          body: JSON.stringify(orderInput),
        },
      );

      // If access token is invalid/expired → try refresh
      if (response.status === 401) {
        const refreshResponse = await fetch(
          `${API_BASE_URL}auth/trader/jwt/refresh/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refresh"),
            }),
          },
        );

        if (!refreshResponse.ok) {
          // refresh failed → logout
          localStorage.removeItem("access");
          localStorage.removeItem("authCredentials");
          localStorage.removeItem("refresh");
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          navigate("/signin");
          return; // ⬅️ IMPORTANT
        }

        const refreshData = await refreshResponse.json();
        localStorage.setItem("access", refreshData.access);

        // retry original request with new access token
        response = await fetch(
          `${API_BASE_URL}trading/open-order`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify(orderInput),
          },
        );
      }

      if (!response.ok) {
        let errorData = null;

        try {
          errorData = await response.json();
        } catch (err) {
          errorData = null;
        }

        if (
          response.status === 400 &&
          errorData?.detail === PENDING_PAYOUT_ORDER_BLOCK_DETAIL
        ) {
          if (typeof onTradeNotification === "function") {
            onTradeNotification({
              type: "pending_payout_order_block",
              tone: "warning",
              message:
                "هنگام پردازش درخواست برداشت، مجاز به انجام معامله نیستید.",
            });
          }
          return;
        }

        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
      resetInputs();
    } catch (error) {
      console.error("Error fetching account data:", error);
    } finally {
      setIsSubmittingOrder(false);
    }
  };
  useEffect(() => {
    if (!Number(volume)) {
      setVolume("");
      setVolumeDisplay("");
    }
  }, [volume]);
  return (
    <>
      <div className="w-full rounded-2xl flex flex-col gap-2.5 bg-zi-bg2-light dark:bg-zi-bg2-dark">
        <div className="w-full border-b border-b-zi-border-dark/50 dark:border-zi-border-light/50">
          <div className="w-full px-2.5 py-1.5 rounded-lg text-sm flex items-center justify-between bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark mb-2.5">
            <span className="opacity-50">اهرم معاملاتی</span>
            <span className="font-medium">
              {accountData?.account?.leverage}x
            </span>
          </div>
        </div>

        <InputSelect
          options={[
            { value: "Limit", label: "لیمیت" },
            { value: "Market", label: "مارکت" },
            { value: "StopLimit", label: "استاپ لیمیت" },
            { value: "StopMarket", label: "استاپ مارکت" },
          ]}
          id="orderType"
          name="orderType"
          value={orderType}
          onChange={setOrderType}
        />

        {/* Limit Inputs */}
        {orderType === "Limit" && (
          <InputText
            label="قیمت"
            value={limitPriceDisplay}
            onChange={(e) => {
              handlePriceChange(
                e.target.value,
                setLimitPrice,
                setLimitPriceDisplay,
              );
            }}
            unit="USDT"
            id="price"
            name="price"
          />
        )}

        {/* Market Inputs */}
        {orderType === "Market" && (
          <InputText
            label="بهترین قیمت بازار"
            unit="USDT"
            id="marketPrice"
            name="marketPrice"
            lock={true}
          />
        )}

        {/* StopLimit Inputs */}
        {orderType === "StopLimit" && (
          <>
            <InputText
              label="قیمت توقف"
              unit="USDT"
              id="stopPrice"
              name="stopPrice"
              value={slStopPriceDisplay}
              onChange={(e) => {
                handlePriceChange(
                  e.target.value,
                  setSlStopPrice,
                  setSlStopPriceDisplay,
                );
              }}
            />
            <InputText
              label="قیمت"
              unit="USDT"
              id="price"
              name="price"
              value={slLimitPriceDisplay}
              onChange={(e) => {
                handlePriceChange(
                  e.target.value,
                  setSlLimitPrice,
                  setSlLimitPriceDisplay,
                );
              }}
            />
          </>
        )}

        {/* StopMarket Inputs */}
        {orderType === "StopMarket" && (
          <>
            <InputText
              label="قیمت توقف"
              unit="USDT"
              id="stopPrice"
              name="stopPrice"
              value={smStopPriceDisplay}
              onChange={(e) => {
                handlePriceChange(
                  e.target.value,
                  setSmStopPrice,
                  setSmStopPriceDisplay,
                );
              }}
            />
            <InputText
              label="بهترین قیمت بازار"
              unit="USDT"
              id="marketPrice"
              name="marketPrice"
              lock={true}
            />
          </>
        )}

        {/* حجم */}
        <InputText
          label="حجم"
          unit="USDT"
          value={volumeDisplay}
          onChange={handleVolumeChange}
          id="volume"
          name="volume"
        />

        {/* Slider – drives percentValue directly */}
        <InputRange value={percentValue} setValue={handlePercentChange} />

        <div className="w-full flex items-center gap-1 text-xs py-0.5 text-zi-text-light/50 dark:text-zi-text-dark/50 divide-x divide-x-reverse divide-zi-border-dark/50 dark:divide-zi-border-light/50">
          <span className="w-full text-center">
            مبلغ {freeMargin ? volumeDisplay || 0 : 0} USDT
          </span>
          <span className="w-full text-center">
            حداکثر {Number(freeMargin).toFixed(2)} USDT
          </span>
        </div>

        <div className="w-full flex flex-col gap-1">
          <InputText
            label="حد سود"
            unit="USDT"
            id="TP"
            name="TP"
            value={tpPriceDisplay}
            onChange={(e) => {
              handlePriceChange(e.target.value, setTpPrice, setTpPriceDisplay);
            }}
          />
          {estimatedTpPnl !== null && (
            <span
              className={`px-2.5 text-xs ${
                estimatedTpPnlMeta?.tone === "profit"
                  ? "text-zi-green-dark"
                  : "text-zi-red-dark"
              }`}
            >
              {estimatedTpPnlMeta?.label}: {formatEstimatedPnl(estimatedTpPnl)}{" "}
              USDT{estimatedTpPnlMeta?.suffix}
            </span>
          )}
          {tpSlValidationMessages.tp && (
            <span className="px-2.5 text-xs text-zi-red-dark">
              {tpSlValidationMessages.tp}
            </span>
          )}
        </div>
        <div className="w-full flex flex-col gap-1">
          <InputText
            label="حد ضرر"
            unit="USDT"
            id="SL"
            name="SL"
            value={slPriceDisplay}
            onChange={(e) => {
              handlePriceChange(e.target.value, setSlPrice, setSlPriceDisplay);
            }}
          />
          {estimatedSlPnl !== null && (
            <span
              className={`px-2.5 text-xs ${
                estimatedSlPnlMeta?.tone === "profit"
                  ? "text-zi-green-dark"
                  : "text-zi-red-dark"
              }`}
            >
              {estimatedSlPnlMeta?.label}: {formatEstimatedPnl(estimatedSlPnl)}{" "}
              USDT{estimatedSlPnlMeta?.suffix}
            </span>
          )}
          {tpSlValidationMessages.sl && (
            <span className="px-2.5 text-xs text-zi-red-dark">
              {tpSlValidationMessages.sl}
            </span>
          )}
        </div>
      </div>

      <button
        onClick={handleOrder}
        disabled={!canTrade || isSubmittingOrder}
        className={`w-full rounded-full py-2 flex justify-center items-center outline-none text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-offset-1 hocus:ring-zi-bg2-light ${
          selectedSide === "buy"
            ? canTrade
              ? "bg-zi-green-dark hocus:ring-offset-zi-green-dark"
              : "bg-gray-600 cursor-not-allowed"
            : canTrade
              ? "bg-zi-red-dark hocus:ring-offset-zi-red-dark"
              : "bg-gray-600 cursor-not-allowed"
        }`}
      >
        {isSubmittingOrder ? (
          <Loader classname="py-3"/>
        ) : (
          <>سفارش {selectedSide === "buy" ? "خرید" : "فروش"}</>
        )}
      </button>
    </>
  );
}
